import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FocusLock from 'react-focus-lock';
import Button from '~/Components/Global/Form/Button';
import { closeModal, setModalData, openModal } from '~/Redux/Modules/Modal';
import { REPEAT_OPTIONS_MODAL, TASK_SETTINGS_MODAL } from '~/Config/Modals';
import RepeatRule from '~/Components/Pages/Calendar/RepeatRule/RepeatRule';
import { showConfirmWithSettings } from '~/Redux/Modules/EventConfirmation';
import { WEEK_TODO, MONTH_TODO } from '~/Config/Checklists';

import './RepeatOptionsModal.scss';

const RepeatOptionsModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(state => state.Modal.modalsOpen.includes(REPEAT_OPTIONS_MODAL));
  const modalInfo = useSelector(state => state.Modal.modalData);

  const task = isOpen && modalInfo?.modalType === REPEAT_OPTIONS_MODAL ? modalInfo.task : null;
  const onSaveCallback =
    isOpen && modalInfo?.modalType === REPEAT_OPTIONS_MODAL ? modalInfo.onSave : null;
  const onCancelCallback =
    isOpen && modalInfo?.modalType === REPEAT_OPTIONS_MODAL ? modalInfo.onCancel : null;
  const positionRect =
    isOpen && modalInfo?.modalType === REPEAT_OPTIONS_MODAL ? modalInfo.positionRect : null;

  const [repeatRule, setRepeatRule] = useState('');
  const [originalRepeatRule, setOriginalRepeatRule] = useState('');

  const handleBack = () => {
    dispatch(closeModal(REPEAT_OPTIONS_MODAL));
    dispatch(setModalData(null));

    if (task) {
      dispatch(
        setModalData({
          task, // Keep original task pristine
          onSave: onSaveCallback,
          onCancel: onCancelCallback,
          positionRect,
          modalType: TASK_SETTINGS_MODAL,
        })
      );
      dispatch(openModal(TASK_SETTINGS_MODAL));
    }
  };

  const handleUpdateRepeat = () => {
    if (task?.repeat_rule) {
      // Existing recurring task - set pending changes without immediate confirmation
      dispatch(closeModal(REPEAT_OPTIONS_MODAL));
      dispatch(
        setModalData({
          task, // Keep original task pristine
          onSave: onSaveCallback,
          onCancel: onCancelCallback,
          positionRect,
          modalType: TASK_SETTINGS_MODAL,
          pendingRepeatRule: repeatRule,
          // Don't set pendingRepeatConfirm here - let TaskSettingsModal handle it during save
        })
      );
      dispatch(openModal(TASK_SETTINGS_MODAL));
    } else {
      // New recurring task - set pending changes
      dispatch(closeModal(REPEAT_OPTIONS_MODAL));
      dispatch(
        setModalData({
          task, // Keep original task pristine
          onSave: onSaveCallback,
          onCancel: onCancelCallback,
          positionRect,
          modalType: TASK_SETTINGS_MODAL,
          pendingRepeatRule: repeatRule,
          pendingRepeatConfirm: 'all',
        })
      );
      dispatch(openModal(TASK_SETTINGS_MODAL));
    }
  };

  const handleRepeatRuleChange = rule => {
    setRepeatRule(rule);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('repeat-options-open');

      const taskRepeatRule = task?.repeat_rule || '';
      setRepeatRule(taskRepeatRule);
      setOriginalRepeatRule(taskRepeatRule);

      return () => {
        document.body.classList.remove('repeat-options-open');
      };
    }
  }, [isOpen, task]);

  if (!isOpen || !modalInfo || modalInfo.modalType !== REPEAT_OPTIONS_MODAL) return null;

  let style = {};
  if (positionRect) {
    style = {
      position: 'absolute',
      // RepeatOptionsModal is ~400px tall (header + repeat rule options + footer)
      top:
        positionRect.top > window.innerHeight - 400 ? window.innerHeight - 400 : positionRect.top,
      // RepeatOptionsModal is 280px wide, so half is 140px
      left:
        positionRect.left > window.innerWidth - 140 ? window.innerWidth - 140 : positionRect.left,
      transform: 'translateX(-50%)',
    };
  }

  const handleWrapperClick = () => {
    handleBack();
  };

  return (
    <div className="repeat-options-modal-wrapper" onClick={handleWrapperClick}>
      <div
        className="repeat-options-modal"
        style={style}
        onClick={e => e.stopPropagation()}
        data-stop-drop-down-close
        role="dialog"
        aria-modal="true"
        aria-labelledby="repeat-options-modal-title"
      >
        <FocusLock>
          <div className="repeat-options-modal__header" id="repeat-options-modal-title">
            <button
              className="back-button"
              onClick={handleBack}
              aria-label="Go back to task settings"
            >
              <span className="material-icons-outlined back-button-icon">chevron_left</span>
            </button>
            <span>Repeat Options</span>
          </div>

          <div className="repeat-options-modal__body">
            <RepeatRule
              repeatable={task || {}}
              onChange={handleRepeatRuleChange}
              singleColumn
              allDay
              allowNever
              weekOnly={task?.type === WEEK_TODO}
              monthOnly={task?.type === MONTH_TODO}
            />
          </div>

          <div className="repeat-options-modal__footer">
            <Button
              text="UPDATE REPEAT"
              className="btn btn-default update-repeat-button"
              onClick={handleUpdateRepeat}
            />
          </div>
        </FocusLock>
      </div>
    </div>
  );
};

export default RepeatOptionsModal;
